import AmbassadorService from "services/AmbassadorService"
import { APP_PREFIX_PATH } from "configs/AppConfig"
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"

export const initialState = {
  ambassador: {},
  ambassadorCount: null,
  ambassadors: [],
  currentPage: 1,
  deleteModalIsOpen: false,
  loadedPages: [],
  loadedSearchPages: [],
  loading: false,
  message: "",
  redirect: "",
  searchValue: null,
  showMessage: false,
  totalItems: null,
  viewModalIsOpen: false,
}

// THUNKS 
export const createAmbassador = createAsyncThunk(
  "ambassador/create",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await AmbassadorService.create(data)
      dispatch(clearStates())
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error")
    }
  }
)

export const getAllAmbassadors = createAsyncThunk(
  "ambassador/getAll",
  async (page, { rejectWithValue }) => {
    try {
      const response = await AmbassadorService.getAll(page)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error")
    }
  }
)

export const getAmbassadorById = createAsyncThunk(
  "ambassador/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AmbassadorService.getById(id)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error")
    }
  }
)

export const searchAmbassadors = createAsyncThunk(
  "ambassador/search",
  async ({ page = 1, value }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setCurrentPage(page))
      const response = await AmbassadorService.search(page, value)
      dispatch(clearStates())
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error")
    }
  }
)

export const deleteAmbassadorById = createAsyncThunk(
  "ambassador/delete",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await AmbassadorService.deleteById(id)
      dispatch(clearStates())
      dispatch(setCurrentPage(1))
      dispatch(getAllAmbassadors(1))
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error")
    }
  }
)

// UPDATE STATUS 
export const updateStatus = createAsyncThunk(
  "ambassador/updateStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await AmbassadorService.updateStatus(id, status)
      return { id, status: response.status || status } // keep it simple
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error")
    }
  }
)

// === SLICE ===
export const ambassadorSlice = createSlice({
  extraReducers: (builder) => {
    builder
      // CREATE
      .addCase(createAmbassador.pending, (state) => {
        state.loading = true
      })
      .addCase(createAmbassador.fulfilled, (state) => {
        state.loading = false
        state.redirect = `${APP_PREFIX_PATH}/ambassadors`
      })
      .addCase(createAmbassador.rejected, (state, action) => {
        state.loading = false
        state.message = action.payload
        state.showMessage = true
      })

      // GET ALL
      .addCase(getAllAmbassadors.pending, (state) => {
        state.loading = true
      })
      .addCase(getAllAmbassadors.fulfilled, (state, action) => {
        state.loading = false
        state.ambassadors = action.payload.data
        state.totalItems = action.payload.totalItems
      })
      .addCase(getAllAmbassadors.rejected, (state, action) => {
        state.loading = false
        state.message = action.payload
        state.showMessage = true
      })

      // SEARCH
      .addCase(searchAmbassadors.pending, (state) => {
        state.loading = true
      })
      .addCase(searchAmbassadors.fulfilled, (state, action) => {
        state.loading = false
        state.ambassadors = action.payload.data
        state.totalItems = action.payload.totalItems
      })
      .addCase(searchAmbassadors.rejected, (state, action) => {
        state.loading = false
        state.message = action.payload
        state.showMessage = true
      })

      // DELETE
      .addCase(deleteAmbassadorById.pending, (state) => {
        state.loading = true
        state.deleteModalIsOpen = false
      })
      .addCase(deleteAmbassadorById.fulfilled, (state) => {
        state.loading = false
        state.deleteModalIsOpen = false
      })
      .addCase(deleteAmbassadorById.rejected, (state, action) => {
        state.loading = false
        state.message = action.payload
        state.showMessage = true
        state.deleteModalIsOpen = false
      })

      // GET BY ID
      .addCase(getAmbassadorById.pending, (state) => {
        state.loading = true
      })
      .addCase(getAmbassadorById.fulfilled, (state, action) => {
        state.loading = false
        state.ambassador = action.payload
      })
      .addCase(getAmbassadorById.rejected, (state, action) => {
        state.loading = false
        state.message = action.payload
        state.showMessage = true
      })

      // UPDATE STATUS 
      .addCase(updateStatus.pending, (state) => {
        state.loading = true
      })
      .addCase(updateStatus.fulfilled, (state, action) => {
        state.loading = false
        const index = state.ambassadors.findIndex(
          (a) => a.id === action.payload.id
        )
        if (index !== -1) {
          state.ambassadors[index].status = action.payload.status
        }
      })
      .addCase(updateStatus.rejected, (state, action) => {
        state.loading = false
        state.message = action.payload
        state.showMessage = true
      })
  },
  initialState,
  name: "ambassador",
  reducers: {
    clearStates: (state) => {
      state.ambassadors = []
      state.totalItems = null
    },
    hideAmbassadorMessage: (state) => {
      state.message = ""
      state.showMessage = false
    },
    openViewModal: (state, action) => {
      state.viewModalIsOpen = action.payload 
    },
    setAmbassadorInfo: (state, action) => {
      state.ambassador = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setRedirect: (state, action) => {
      state.redirect = action.payload
    },
    setSearchValue: (state, action) => {
      state.searchValue = action.payload
    },
    showAmbassadorMessage: (state, action) => {
      state.message = action.payload
      state.showMessage = true
      state.loading = false
    },
    showDeleteModal: (state, action) => {
      state.deleteModalIsOpen = action.payload.etat
    },
     showLoading: (state) => {
      state.loading = true
    },
  },
})

export const {
  clearStates,
  hideAmbassadorMessage,
  openViewModal,
  setAmbassadorInfo,
  setCurrentPage,
  setRedirect,
  setSearchValue,
  showAmbassadorMessage,
  showDeleteModal,
  showLoading,
} = ambassadorSlice.actions

export default ambassadorSlice.reducer
